import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { Upload, File, X, CheckCircle, Loader2 } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

export function FileUploader({ onUploadComplete, dealId, clientId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

  const onDrop = useCallback((acceptedFiles) => {
    const validFiles = acceptedFiles.filter(f => {
      if (f.size > MAX_FILE_SIZE) {
        toast.error(`${f.name} exceeds 25MB limit`);
        return false;
      }
      return true;
    });
    const newFiles = validFiles.map(file => ({
      file,
      name: file.name,
      size: file.size,
      progress: 0,
      status: 'pending'
    }));
    if (newFiles.length > 0) setFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'text/*': ['.txt', '.csv'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    }
  });

  const uploadFiles = async () => {
    if (files.length === 0) return;
    setUploading(true);

    for (let i = 0; i < files.length; i++) {
      if (files[i].status === 'uploaded') continue;

      try {
        setFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'uploading', progress: 50 } : f
        ));

        const result = await api.uploadViaPresigned(files[i].file, {
          dealId: dealId,
          clientId: clientId
        });

        setFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'uploaded', progress: 100, result } : f
        ));

        onUploadComplete?.(result);
      } catch (error) {
        setFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'error', progress: 0 } : f
        ));
        toast.error(`Failed to upload ${files[i].name}`);
      }
    }

    setUploading(false);
    toast.success('Files uploaded successfully');
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        data-testid="upload-dropzone"
        className={`dropzone ${isDragActive ? 'active' : ''}`}
      >
        <input {...getInputProps()} />
        <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">
          {isDragActive ? 'Drop files here' : 'Drag & drop files or click to browse'}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          PDF, Images, Word documents
        </p>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={index}
              data-testid="upload-item"
              className="flex items-center gap-3 p-3 rounded-lg border bg-card"
            >
              <File className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{file.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
                {file.status === 'uploading' && (
                  <Progress value={file.progress} className="mt-1 h-1" />
                )}
              </div>
              {file.status === 'uploaded' ? (
                <CheckCircle className="h-5 w-5 text-success flex-shrink-0" />
              ) : file.status === 'uploading' ? (
                <Loader2 className="h-5 w-5 animate-spin text-primary flex-shrink-0" />
              ) : (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 flex-shrink-0"
                  onClick={() => removeFile(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}

          <Button
            onClick={uploadFiles}
            disabled={uploading || files.every(f => f.status === 'uploaded')}
            className="w-full"
            data-testid="review-and-save-button"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              'Upload All Files'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
